import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "../../contexts/AuthContext";
import {
  UserCircleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CameraIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import api, { apiUpload } from "../../services/api";
import { getImageUrl } from "../../services/api";
import { User } from "../../types";

interface ProfileFormData {
  name: string;
  email: string;
  age?: number;
  village?: string;
  district?: string;
  province?: string;
  date_of_birth?: string;
  phone?: string;
}

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [updatedUser, setUpdatedUser] = useState<User | null>(null);
  const [isImageEditing, setIsImageEditing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileFormData>();

  useEffect(() => {
    if (user) {
      // Format the date_of_birth to YYYY-MM-DD for the HTML date input
      let formattedDate = user.date_of_birth;
      if (user.date_of_birth) {
        try {
          // Try to format the date properly for the date input
          const date = new Date(user.date_of_birth);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split("T")[0];
          }
        } catch (e) {
          console.error("Error formatting date:", e);
        }
      }

      reset({
        name: user.name,
        email: user.email,
        age: user.age,
        village: user.village,
        district: user.district,
        province: user.province,
        date_of_birth: formattedDate,
        phone: user.phone,
      });
    }
  }, [user, reset]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        setError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError("File size must be less than 5MB");
        return;
      }

      // Clear any previous errors
      setError("");
      setProfileImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      // Only update profile image if in image editing mode
      if (isImageEditing) {
        if (!profileImage) {
          setError("Please select an image to upload");
          setIsLoading(false);
          return;
        }

        const formData = new FormData();

        // Add profile image
        console.log("Adding profile image to form data:", {
          name: profileImage.name,
          type: profileImage.type,
          size: profileImage.size,
        });
        formData.append("profile_image", profileImage);

        // Use apiUpload for file uploads to avoid Content-Type conflicts
        const response = await apiUpload.put("/users/profile", formData);

        console.log("Profile image update response:", response.data);

        // Update user context with the new profile image
        if (response.data.user) {
          updateUser(response.data.user);
          setUpdatedUser(response.data.user);
          setSuccess("ອັບເດດຮູບໂປຣໄຟລ໌ສຳເລັດແລ້ວ!");
          setIsImageEditing(false);
          setProfileImage(null);
          setImagePreview(null);
        } else {
          setError("ບໍ່ສາມາດອັບເດດຮູບໂປຣໄຟລ໌ໄດ້. ກະລຸນາລອງໃໝ່.");
        }
      } else {
        // Process and validate the form data for full profile update
        const processedData = { ...data };

        // Format date properly if provided
        if (processedData.date_of_birth) {
          try {
            // Ensure it's in YYYY-MM-DD format
            const date = new Date(processedData.date_of_birth);
            if (!isNaN(date.getTime())) {
              processedData.date_of_birth = date.toISOString().split("T")[0];
            }
          } catch (e) {
            console.error("Error formatting date for submission:", e);
            // Keep the original value if parsing fails
          }
        }

        // Convert age to number or null
        if (
          processedData.age !== undefined &&
          processedData.age !== null &&
          String(processedData.age).trim() !== ""
        ) {
          const numAge = Number(processedData.age);
          if (isNaN(numAge) || numAge < 1 || numAge > 120) {
            processedData.age = undefined; // Will be skipped in form data
          } else {
            processedData.age = numAge;
          }
        } else {
          processedData.age = undefined;
        }

        console.log("Processed form data:", processedData);

        const formData = new FormData();

        // Add form fields - safely handle null/undefined values
        Object.entries(processedData).forEach(([key, value]) => {
          // Only append if value is not null, undefined, or empty string
          if (value !== null && value !== undefined && value !== "") {
            formData.append(key, String(value)); // Use String() instead of toString() to handle all types safely
          } else {
            console.log(`Skipping null/empty field: ${key}`);
          }
        });

        // Add profile image if selected
        if (profileImage) {
          console.log("Adding profile image to form data:", {
            name: profileImage.name,
            type: profileImage.type,
            size: profileImage.size,
          });
          formData.append("profile_image", profileImage);
        }

        // Log form data contents
        console.log("Form data entries:");
        for (let [key, value] of formData.entries()) {
          console.log(key, typeof value === "object" ? "File object" : value);
        }

        // Use apiUpload if there's a file, otherwise use regular api
        let response;
        if (profileImage) {
          // Use apiUpload for file uploads to avoid Content-Type conflicts
          response = await apiUpload.put("/users/profile", formData);
        } else {
          // For non-file updates, convert FormData to JSON
          const updateData: any = {};
          for (let [key, value] of formData.entries()) {
            updateData[key] = value;
          }
          response = await api.put("/users/profile", updateData);
        }

        console.log("Profile update response:", response.data);

        // Update user context
        if (response.data.user) {
          updateUser(response.data.user);
          setUpdatedUser(response.data.user);
          setSuccess("ອັບເດດໂປຣໄຟລ໌ສຳເລັດແລ້ວ!");
          setIsEditing(false);
          setProfileImage(null);
          setImagePreview(null);
        } else {
          setError("ບໍ່ສາມາດອັບເດດໂປຣໄຟລ໌ໄດ້. ກະລຸນາລອງໃໝ່.");
        }
      }
    } catch (err: any) {
      console.error("Profile update error:", err);

      // Provide more specific error messages
      if (err.response?.data?.errors && err.response.data.errors.length > 0) {
        // Handle validation errors
        const errorMessages = err.response.data.errors
          .map((error: any) => error.msg)
          .join(", ");
        setError(errorMessages);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 413) {
        setError("File size too large. Please select a smaller image.");
      } else if (err.response?.status === 400) {
        setError(
          "Invalid file or data. Please check your inputs and try again.",
        );
      } else if (err.message) {
        setError(`Upload failed: ${err.message}`);
      } else {
        setError("ການອັບເດດໂປຣໄຟລ໌ລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsImageEditing(false);
    setError("");
    setSuccess("");
    setProfileImage(null);
    setImagePreview(null);
    if (user) {
      reset({
        name: user.name,
        email: user.email,
        age: user.age,
        village: user.village,
        district: user.district,
        province: user.province,
        date_of_birth: user.date_of_birth,
        phone: user.phone,
      });
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-6">
      {/* Header with gradient background */}
      <div className="mb-4 sm:mb-6 lg:mb-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold truncate">
              ໂປຣໄຟລ໌ຂອງຂ້ອຍ
            </h1>
            <p className="mt-1 sm:mt-2 text-sm sm:text-base lg:text-lg text-indigo-100 line-clamp-2">
              ຈັດການຂໍ້ມູນສ່ວນຕົວແລະການຕັ້ງຄ່າຂອງທ່ານ
            </p>
          </div>
          <UserCircleIcon className="h-8 w-8 sm:h-12 sm:w-12 lg:h-16 lg:w-16 text-yellow-300 opacity-50 flex-shrink-0 ml-2" />
        </div>
      </div>

      {/* Profile Content */}
      <div className="bg-white rounded-lg sm:rounded-xl shadow-lg overflow-hidden">
        {/* Profile Header */}
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
          {isImageEditing && (
            <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs sm:text-sm text-blue-800 leading-relaxed">
                ຕອນນີ້ທ່ານສາມາດແກ້ໄຂຮູບໂປຣໄຟລ໌ເທົ່ານັ້ນ.
                ຫາກຕ້ອງການແກ້ໄຂຂໍ້ມູນອື່ນໆກະລຸນາຕິດຕໍ່ຜູ້ດູແລຜ່ານກ໋ອງຂໍ້ຄວາມ.
              </p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              ຂໍ້ມູນສ່ວນຕົວ
            </h2>
            {!isEditing && !isImageEditing ? (
              <button
                onClick={() => setIsImageEditing(true)}
                className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105 w-full sm:w-auto justify-center"
              >
                <PencilIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                ແກ້ໄຂຮູບໂປຣໄຟລ໌ເທົ່ານັ້ນ
              </button>
            ) : isImageEditing ? (
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-all transform hover:scale-105"
                >
                  <CheckIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  {isLoading ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຮູບໂປຣໄຟລ໌"}
                </button>
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105"
                >
                  <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  ຍົກເລີກ
                </button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-all transform hover:scale-105"
                >
                  <CheckIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  {isLoading ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກການປ່ຽນແປງ"}
                </button>
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105"
                >
                  <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  ຍົກເລີກ
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Profile Form */}
        <div className="p-3 sm:p-4 lg:p-6">
          {error && (
            <div className="mb-3 sm:mb-4 rounded-md bg-red-50 p-3 sm:p-4">
              <div className="text-xs sm:text-sm text-red-800">{error}</div>
            </div>
          )}

          {success && (
            <div className="mb-3 sm:mb-4 rounded-md bg-green-50 p-3 sm:p-4">
              <div className="text-xs sm:text-sm text-green-800">{success}</div>
            </div>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 sm:space-y-6"
          >
            {/* Profile Image */}
            <div className="sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Profile Picture
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center shadow-md mx-auto sm:mx-0">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : updatedUser?.profile_image || user?.profile_image ? (
                    <img
                      src={getImageUrl(
                        updatedUser?.profile_image || user?.profile_image || "",
                      )}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const imagePath =
                          updatedUser?.profile_image || user?.profile_image;
                        console.error(
                          "Failed to load profile image:",
                          imagePath,
                        );
                        console.error(
                          "Attempted URL:",
                          getImageUrl(imagePath || ""),
                        );
                        e.currentTarget.src = "";
                        e.currentTarget.onerror = null;
                      }}
                      key={
                        updatedUser?.profile_image ||
                        user?.profile_image ||
                        "default"
                      }
                    />
                  ) : (
                    <UserCircleIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                  )}
                </div>
                {(isEditing || isImageEditing) && (
                  <div className="text-center sm:text-left">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="profile-image"
                    />
                    <label
                      htmlFor="profile-image"
                      className="cursor-pointer inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105"
                    >
                      <CameraIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                      Change Photo
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      JPG, PNG, GIF or WebP. Max 5MB.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ລະຫັດນັກຮຽນ (Student ID)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={
                      user.student_id ||
                      `STD-${String(user.id).padStart(5, "0")}`
                    }
                    disabled={true}
                    aria-label="Student ID"
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed text-xs sm:text-sm"
                  />
                  <UserCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  ລະຫັດນັກຮຽນບໍ່ສາມາດແກ້ໄຂໄດ້
                </p>
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ລະຫັດລົງທະບຽນຮຽນ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={user.registration_code || "-"}
                    disabled={true}
                    aria-label="Registration Code"
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500 cursor-not-allowed text-xs sm:text-sm"
                  />
                  <UserCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  ລະຫັດລົງທະບຽນຮຽນບໍ່ສາມາດແກ້ໄຂໄດ້
                </p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ຊື່ເຕັມ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register("name", { required: "Name is required" })}
                    disabled={!isEditing}
                    className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border text-xs sm:text-sm ${
                      errors.name ? "border-red-300" : "border-gray-300"
                    } focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500`}
                  />
                  <UserCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
                {errors.name && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ອີເມວ
                </label>
                <div className="relative">
                  <input
                    type="email"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    disabled={!isEditing}
                    className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border text-xs sm:text-sm ${
                      errors.email ? "border-red-300" : "border-gray-300"
                    } focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500`}
                  />
                  <EnvelopeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ເບີໂທລະສັບ
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    {...register("phone")}
                    disabled={!isEditing}
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500 text-xs sm:text-sm"
                  />
                  <PhoneIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ວັນເດືອນປີເກີດ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD (ຫຼື ຄລິກເພື່ອເລືອກວັນທີ)"
                    {...register("date_of_birth")}
                    disabled={!isEditing}
                    onFocus={(e) => {
                      if (isEditing) {
                        e.target.type = "date";
                        e.target.click();
                      }
                    }}
                    onBlur={(e) => {
                      if (isEditing) {
                        e.target.type = "text";
                      }
                    }}
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500 text-xs sm:text-sm"
                  />
                  <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  ສາມາດພິມວັນທີໄດ້ ຫຼື ຄລິກເພື່ອເລືອກຈາກປະຕິທິນ
                </p>
              </div>
            </div>

            {/* Location Information */}
            <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ບ້ານ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register("village")}
                    disabled={!isEditing}
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500 text-xs sm:text-sm"
                  />
                  <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ເມືອງ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register("district")}
                    disabled={!isEditing}
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500 text-xs sm:text-sm"
                  />
                  <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ແຂວງ
                </label>
                <div className="relative">
                  <input
                    type="text"
                    {...register("province")}
                    disabled={!isEditing}
                    className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500 text-xs sm:text-sm"
                  />
                  <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Profile Tips */}
      <div className="mt-6 sm:mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl sm:rounded-2xl p-3 border border-indigo-200">
        <div className="flex flex-col sm:flex-row items-center gap-2">
          <div className="bg-white rounded-full p-2 sm:p-3 shadow-sm">
            <SparklesIcon className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600" />
          </div>
          <h6 className="text-gray-900 ">
            ໃຫ້ແນ່ໃຈວ່າຂໍ້ມູນໂປຣໄຟລ໌ຂອງທ່ານຖືກຕ້ອງ.
          </h6>
        </div>
      </div>
    </div>
  );
};

export default Profile;
